import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { parseLocaleDecimal } from '../../../../utils/localeNumber';
import { DecimalNumberInput } from '../../../../components/ui';
import { PaymentEntry } from '../../../../types/sales.types';
import { PAYMENT_METHODS } from '../../../../utils/constants/paymentMethods';
import SalesStepHint from '../SalesStepHint/SalesStepHint';
import { useTabScopedOpen } from '../../../dashboard/TabPanelActiveContext';

const ModalCancelButton = styled(Button)(({ theme }) => ({
  boxShadow: 'none',
  backgroundColor: 'transparent',
  color: theme.palette.text.secondary,
  border: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    boxShadow: 'none',
  },
}));

const ModalActionButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(to right, #06b6d4, #10b981)',
  color: '#ffffff',
  boxShadow: 'none',
  '&:hover': {
    background: 'linear-gradient(to right, #0891b2, #059669)',
    boxShadow: 'none',
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled,
    background: theme.palette.action.disabledBackground,
  },
}));

export type PaymentAmountSubmitHandler = (
  method: string,
  amount: number,
  cashReceived?: number,
  notes?: string,
  bankReference?: string,
  qrTransactionId?: string,
) => void;

export type PaymentAmountUpdateHandler = (
  paymentId: string,
  amount: number,
  cashReceived?: number,
  notes?: string,
  bankReference?: string,
  qrTransactionId?: string,
) => void;

export interface PaymentAmountDialogProps {
  open: boolean;
  onClose: () => void;
  method: string | null;
  editingPayment: PaymentEntry | null;
  remainingBalance: number;
  onAdd: PaymentAmountSubmitHandler;
  onUpdate: PaymentAmountUpdateHandler;
  formatCurrency: (amount: number) => string;
  /** When true (default), new payments start with the remaining balance filled in. */
  prefillRemainingAmount?: boolean;
}

/**
 * Shared add/edit payment amount dialog for nueva venta, intercambio and procesar pago.
 * Cash uses separate applied amount + optional tender (cambio); card/QR keep reference fields.
 */
const PaymentAmountDialog: React.FC<PaymentAmountDialogProps> = ({
  open,
  onClose,
  method,
  editingPayment,
  remainingBalance,
  onAdd,
  onUpdate,
  formatCurrency,
  prefillRemainingAmount = true,
}) => {
  const scopedOpen = useTabScopedOpen(open);
  const [amount, setAmount] = useState('');
  const [cashReceived, setCashReceived] = useState('');
  const [notes, setNotes] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [qrTransactionId, setQrTransactionId] = useState('');
  const isCash = method === PAYMENT_METHODS.CASH;
  const isCard = method === PAYMENT_METHODS.CARD;
  const isQr = method === PAYMENT_METHODS.QR;
  const isEditing = editingPayment != null;
  const effectiveRemaining =
    isEditing && editingPayment != null
      ? remainingBalance + editingPayment.amount
      : remainingBalance;
  const parsedAmount = parseLocaleDecimal(amount) ?? 0;
  const parsedCashReceived = parseLocaleDecimal(cashReceived) ?? 0;

  useEffect(() => {
    if (!open) return;
    if (isEditing && editingPayment) {
      setAmount(editingPayment.amount.toString());
      setCashReceived(editingPayment.cashReceived?.toString() ?? '');
      setNotes(editingPayment.paymentNotes ?? '');
      setBankReference(editingPayment.bankReference ?? '');
      setQrTransactionId(editingPayment.qrTransactionId ?? '');
    } else {
      setAmount(
        prefillRemainingAmount && effectiveRemaining > 0 ? effectiveRemaining.toString() : '',
      );
      setCashReceived('');
      setNotes('');
      setBankReference('');
      setQrTransactionId('');
    }
    // Only re-prefill when the dialog opens or the edited payment changes —
    // not when remainingBalance drifts mid-edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, editingPayment, open, prefillRemainingAmount]);

  const handleSubmit = () => {
    const numAmount = parseLocaleDecimal(amount) ?? 0;
    if (numAmount <= 0 || method == null) return;
    if (numAmount > effectiveRemaining) return;

    const cashReceivedValue =
      isCash && cashReceived.trim() !== ''
        ? (parseLocaleDecimal(cashReceived) ?? undefined)
        : undefined;
    const notesValue = notes.trim() !== '' ? notes.trim() : undefined;
    const bankReferenceValue =
      isCard && bankReference.trim() !== '' ? bankReference.trim() : undefined;
    const qrTransactionIdValue =
      isQr && qrTransactionId.trim() !== '' ? qrTransactionId.trim() : undefined;

    if (isEditing && editingPayment) {
      onUpdate(
        editingPayment.id,
        numAmount,
        cashReceivedValue,
        notesValue,
        bankReferenceValue,
        qrTransactionIdValue,
      );
    } else {
      onAdd(
        method,
        numAmount,
        cashReceivedValue,
        notesValue,
        bankReferenceValue,
        qrTransactionIdValue,
      );
    }
    onClose();
  };

  return (
    <Dialog open={scopedOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar Pago' : 'Agregar Pago'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {method == null && !isEditing && (
            <SalesStepHint tone="success">Seleccione un método de pago.</SalesStepHint>
          )}
          <DecimalNumberInput
            label="Monto del pago"
            value={amount}
            onChange={(val) => setAmount(val != null ? String(val) : '')}
            decimalPlaces={2}
            min={0.01}
            max={999999.99}
            fullWidth
            sx={{ mb: 2 }}
            helperText={`Máximo: ${formatCurrency(effectiveRemaining)}`}
            error={parsedAmount > effectiveRemaining}
            disabled={method == null && !isEditing}
            InputProps={{
              startAdornment: <InputAdornment position="start">Bs</InputAdornment>,
            }}
          />
          {isCash && (
            <DecimalNumberInput
              label="Monto recibido (opcional)"
              value={cashReceived}
              onChange={(val) => setCashReceived(val != null ? String(val) : '')}
              decimalPlaces={2}
              min={0.01}
              max={999999.99}
              fullWidth
              sx={{ mb: 2 }}
              helperText="Si se especifica, se calculará el cambio automáticamente"
              disabled={method == null && !isEditing}
              InputProps={{
                startAdornment: <InputAdornment position="start">Bs</InputAdornment>,
              }}
            />
          )}
          {isCard && (
            <TextField
              label="Referencia de Tarjeta"
              value={bankReference}
              onChange={(e) => setBankReference(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
          {isQr && (
            <TextField
              label="ID de Transacción QR"
              value={qrTransactionId}
              onChange={(e) => setQrTransactionId(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
            />
          )}
          <TextField
            label="Notas (opcional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />
          {isCash && cashReceived.trim() !== '' && parsedCashReceived > parsedAmount && (
            <SalesStepHint tone="success">
              Cambio a entregar: {formatCurrency(parsedCashReceived - parsedAmount)}
            </SalesStepHint>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <ModalCancelButton onClick={onClose}>Cancelar</ModalCancelButton>
        <ModalActionButton
          onClick={handleSubmit}
          variant="contained"
          disabled={
            method == null ||
            amount === '' ||
            parsedAmount <= 0 ||
            parsedAmount > effectiveRemaining
          }
        >
          {isEditing ? 'Guardar' : 'Agregar'}
        </ModalActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentAmountDialog;
